import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const SpeakerEventsPage = () => {
  const { speakerProfile } = useOutletContext();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (speakerProfile) fetchEvents();
  }, [speakerProfile]);

  const fetchEvents = async () => {
    try {
      const { data } = await supabase
        .from('event_speakers')
        .select(`
          session_title,
          events (id, title, start_date, end_date, status, location, image_url,
            venues (name, city, state), organizations (name))
        `)
        .eq('speaker_id', speakerProfile.id);

      const eventsList = (data || [])
        .map(e => ({ ...e.events, session_title: e.session_title }))
        .filter(Boolean)
        .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      setEvents(eventsList);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const filteredEvents = events.filter(event => {
    const eventDate = new Date(event.start_date);
    if (filter === 'upcoming') return eventDate >= now;
    if (filter === 'past') return eventDate < now;
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (status, startDate) => {
    if (new Date(startDate) < now) return { label: 'Completed', class: 'bg-gray-100 text-gray-800' };
    const styles = {
      upcoming: { label: 'Upcoming', class: 'bg-green-100 text-green-800' },
      confirmed: { label: 'Confirmed', class: 'bg-blue-100 text-blue-800' },
      pending: { label: 'Pending', class: 'bg-yellow-100 text-yellow-800' }
    };
    return styles[status] || { label: status, class: 'bg-gray-100 text-gray-600' };
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Events</h1>
          <p className="mt-1 text-sm text-gray-500">Your speaking engagements.</p>
        </div>
        <Link to="/speaker/submissions" className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Submit Event
        </Link>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-fit">
          {['all', 'upcoming', 'past'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm font-medium rounded-md capitalize ${filter === f ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600'}`}>
              {f} ({f === 'all' ? events.length : events.filter(e => f === 'upcoming' ? new Date(e.start_date) >= now : new Date(e.start_date) < now).length})
            </button>
          ))}
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-12 text-center">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          <p className="text-gray-500 text-lg mb-2">No {filter === 'all' ? '' : filter} events</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredEvents.map(event => {
            const status = getStatusBadge(event.status, event.start_date);
            return (
              <div key={event.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      {event.image_url ? (
                        <img src={event.image_url} alt="" className="w-16 h-16 rounded-lg object-cover hidden sm:block" />
                      ) : (
                        <div className="w-16 h-16 bg-indigo-100 rounded-lg items-center justify-center hidden sm:flex">
                          <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        </div>
                      )}
                      <div>
                        <Link to={`/events/${event.id}`} className="text-lg font-semibold text-gray-900 dark:text-white hover:text-indigo-600">{event.title}</Link>
                        {event.session_title && <p className="text-sm text-indigo-600 mt-0.5">Session: {event.session_title}</p>}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
                          <span className="flex items-center"><svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>{formatDate(event.start_date)}</span>
                          {event.venues?.city && <span className="flex items-center"><svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>{event.venues.city}, {event.venues.state}</span>}
                          {event.organizations?.name && <span>{event.organizations.name}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${status.class}`}>{status.label}</span>
                    <Link to={`/events/${event.id}`} className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100">View</Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SpeakerEventsPage;
